import { Logger } from "@shared/utils/logger";
import { STORAGE_KEYS, TIME_CONSTANTS } from "@shared/constants/constant";

// Types
export interface GuestMeetingEntry {
  guestId: string;
  guestName: string;
  guestEmail?: string;
  roomId: string;
  roomName: string;
  joinDate: number; // timestamp in seconds
}

interface GuestStorageData {
  entries: GuestMeetingEntry[];
}

interface GuestRejectionData {
  [roomName: string]: {
    rejected: boolean;
    message: string;
    timestamp: number; // When rejection occurred
  };
}

export interface MediaPreferences {
  micEnabled: boolean;
  cameraEnabled: boolean;
}

const DEFAULT_MEDIA_PREFERENCES: MediaPreferences = {
  micEnabled: true,
  cameraEnabled: true
};

// Guest Storage Utils
export const GuestStorage = {
  /**
   * Get all guest data from local storage
   */
  getStorage: (): GuestStorageData => {
    if (typeof window === "undefined") {
      return { entries: [] };
    }

    try {
      const data = localStorage.getItem(STORAGE_KEYS.CLIENT.GUEST_MEETING_DATA);
      if (!data) {
        return { entries: [] };
      }
      return JSON.parse(data) as GuestStorageData;
    } catch {
      return { entries: [] };
    }
  },

  /**
   * Save guest storage data
   */
  saveStorage: (data: GuestStorageData): void => {
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem(STORAGE_KEYS.CLIENT.GUEST_MEETING_DATA, JSON.stringify(data));
    } catch (error) {
      Logger.error("GuestStorage", "Failed to save:", error);
    }
  },

  /**
   * Clean up entries older than 15 days
   */
  cleanupExpired: (): void => {
    const storage = GuestStorage.getStorage();
    const now = Math.floor(Date.now() / 1000);
    const expirySeconds = TIME_CONSTANTS.GUEST_DATA_EXPIRY_DAYS * TIME_CONSTANTS.SECONDS_PER_DAY;

    const validEntries = storage.entries.filter((entry) => {
      const age = now - entry.joinDate;
      return age < expirySeconds;
    });

    if (validEntries.length !== storage.entries.length) {
      GuestStorage.saveStorage({ entries: validEntries });
    }
  },

  /**
   * Get guest data for a specific room
   */
  getDataForRoom: (roomName: string): GuestMeetingEntry | null => {
    const storage = GuestStorage.getStorage();
    const now = Math.floor(Date.now() / 1000);
    const expirySeconds = TIME_CONSTANTS.GUEST_DATA_EXPIRY_DAYS * TIME_CONSTANTS.SECONDS_PER_DAY;

    const entry = storage.entries.find((e) => e.roomName === roomName);

    if (!entry) return null;

    // Check if expired
    const age = now - entry.joinDate;
    if (age >= expirySeconds) {
      const filtered = storage.entries.filter((e) => e.roomName !== roomName);
      GuestStorage.saveStorage({ entries: filtered });
      return null;
    }

    return entry;
  },

  /**
   * Save guest data after joining a meeting
   */
  saveData: (entry: GuestMeetingEntry): void => {
    const storage = GuestStorage.getStorage();
    const filtered = storage.entries.filter((e) => e.roomName !== entry.roomName);
    filtered.push(entry);
    GuestStorage.saveStorage({ entries: filtered });
  },

  /**
   * Check if user is a returning guest for this room
   */
  isReturning: (roomName: string): boolean => {
    return GuestStorage.getDataForRoom(roomName) !== null;
  },

  /**
   * Remove guest data for a specific room
   */
  removeDataForRoom: (roomName: string): void => {
    const storage = GuestStorage.getStorage();
    const filtered = storage.entries.filter((e) => e.roomName !== roomName);
    GuestStorage.saveStorage({ entries: filtered });
  },

  /**
   * Clear all guest data
   */
  clearAll: (): void => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEYS.CLIENT.GUEST_MEETING_DATA);
    }
  },

  // Rejection Tracking

  /**
   * Save rejection status for a room
   */
  saveRejection: (roomName: string, message: string): void => {
    if (typeof window === "undefined") return;

    try {
      const existing = localStorage.getItem(STORAGE_KEYS.CLIENT.GUEST_REJECTION_DATA);
      const data: GuestRejectionData = existing ? JSON.parse(existing) : {};

      data[roomName] = {
        rejected: true,
        message,
        timestamp: Math.floor(Date.now() / 1000)
      };

      localStorage.setItem(STORAGE_KEYS.CLIENT.GUEST_REJECTION_DATA, JSON.stringify(data));
    } catch (error) {
      Logger.error("GuestStorage", "Failed to save rejection:", error);
    }
  },

  /**
   * Get rejection status for a room
   */
  getRejection: (roomName: string): { rejected: boolean; message: string } | null => {
    if (typeof window === "undefined") return null;

    try {
      const data = localStorage.getItem(STORAGE_KEYS.CLIENT.GUEST_REJECTION_DATA);
      if (!data) return null;

      const parsed: GuestRejectionData = JSON.parse(data);
      const entry = parsed[roomName];

      if (!entry) return null;

      const now = Math.floor(Date.now() / 1000);
      const maxAge = TIME_CONSTANTS.GUEST_REJECTION_EXPIRY_HOURS * TIME_CONSTANTS.SECONDS_PER_HOUR;

      if (now - entry.timestamp > maxAge) {
        delete parsed[roomName];
        localStorage.setItem(STORAGE_KEYS.CLIENT.GUEST_REJECTION_DATA, JSON.stringify(parsed));
        return null;
      }

      return { rejected: entry.rejected, message: entry.message };
    } catch {
      return null;
    }
  },

  /**
   * Clear rejection status for a room
   */
  clearRejection: (roomName: string): void => {
    if (typeof window === "undefined") return;

    try {
      const data = localStorage.getItem(STORAGE_KEYS.CLIENT.GUEST_REJECTION_DATA);
      if (!data) return;

      const parsed: GuestRejectionData = JSON.parse(data);
      delete parsed[roomName];
      localStorage.setItem(STORAGE_KEYS.CLIENT.GUEST_REJECTION_DATA, JSON.stringify(parsed));
    } catch (error) {
      Logger.error("GuestStorage", "Failed to clear rejection:", error);
    }
  }
};

// Media Storage Utils

export const MediaStorage = {
  /**
   * Get stored media preferences
   */
  getPreferences: (): MediaPreferences => {
    if (typeof window === "undefined") {
      return DEFAULT_MEDIA_PREFERENCES;
    }

    try {
      const data = localStorage.getItem(STORAGE_KEYS.CLIENT.MEDIA_PREFERENCES);
      if (!data) {
        return DEFAULT_MEDIA_PREFERENCES;
      }
      const parsed = JSON.parse(data) as MediaPreferences;
      return {
        micEnabled: typeof parsed.micEnabled === "boolean" ? parsed.micEnabled : DEFAULT_MEDIA_PREFERENCES.micEnabled,
        cameraEnabled:
          typeof parsed.cameraEnabled === "boolean" ? parsed.cameraEnabled : DEFAULT_MEDIA_PREFERENCES.cameraEnabled
      };
    } catch {
      return DEFAULT_MEDIA_PREFERENCES;
    }
  },

  /**
   * Save media preferences
   */
  savePreferences: (micEnabled: boolean, cameraEnabled: boolean): void => {
    if (typeof window === "undefined") return;

    try {
      const data: MediaPreferences = { micEnabled, cameraEnabled };
      localStorage.setItem(STORAGE_KEYS.CLIENT.MEDIA_PREFERENCES, JSON.stringify(data));
    } catch (error) {
      Logger.error("MediaStorage", "Failed to save preferences:", error);
    }
  }
};

// Admin Storage Utils

export const AdminStorage = {
  getToken: (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEYS.ADMIN.TOKEN);
  },

  setToken: (token: string): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEYS.ADMIN.TOKEN, token);
  },

  removeToken: (): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(STORAGE_KEYS.ADMIN.TOKEN);
  },

  isAuthenticated: (): boolean => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_KEYS.ADMIN.IS_AUTHENTICATED) === "true";
  },

  setAuthenticated: (value: boolean): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEYS.ADMIN.IS_AUTHENTICATED, String(value));
  },

  removeAuthenticated: (): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(STORAGE_KEYS.ADMIN.IS_AUTHENTICATED);
  },

  getEmail: (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEYS.ADMIN.EMAIL);
  },

  setEmail: (email: string): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEYS.ADMIN.EMAIL, email);
  },

  getName: (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEYS.ADMIN.NAME);
  },

  setName: (name: string): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEYS.ADMIN.NAME, name);
  },

  getLoginTime: (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEYS.ADMIN.LOGIN_TIME);
  },

  setLoginTime: (time: string): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEYS.ADMIN.LOGIN_TIME, time);
  },

  clearSession: (): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(STORAGE_KEYS.ADMIN.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ADMIN.IS_AUTHENTICATED);
    localStorage.removeItem(STORAGE_KEYS.ADMIN.EMAIL);
    localStorage.removeItem(STORAGE_KEYS.ADMIN.NAME);
    localStorage.removeItem(STORAGE_KEYS.ADMIN.LOGIN_TIME);
  }
};
